/**
 * Agent roster — source of truth for /agents.
 *
 * Verified against the actual code under:
 *   shared_backend/apps/orchestrator/      (foundational layer)
 *   shared_backend/apps/code/routers/      (code IDE agents)
 *   shared_backend/apps/research/services/ (research agents)
 *   shared_backend/apps/news/services/     (news agents)
 *   shared_backend/apps/browser/           (browser agent)
 *   shared_backend/apps/workflow/          (workflow runner)
 *   shared_backend/apps/search_index/      (search workers)
 *   shared_data/shared_component_library/components/chat-drawer.tsx (chat modes)
 *
 * Marketing rule: never name underlying models, vendors, or frameworks.
 * Each entry is one-line truth about what it does for the user.
 */

export type AgentStatus = "shipping" | "beta" | "soon";

export type Agent = {
  name: string;
  description: string;
  status: AgentStatus;
};

export type AgentGroup = {
  /** Display kicker (kept short) */
  kicker: string;
  /** Main heading shown above the cards */
  heading: string;
  /** Optional subdomain or surface label */
  surface?: string;
  /** One-line lede shown under the heading */
  lede: string;
  agents: Agent[];
};

export const AGENT_GROUPS: AgentGroup[] = [
  // ─── Foundational orchestration ────────────────────────────────────────────
  {
    kicker: "Foundational",
    heading: "The layer underneath everything.",
    surface: "Shared across apps",
    lede:
      "A small set of always-on pieces that route a prompt to the right specialist, plan multi-step work, and check the result before it reaches you.",
    agents: [
      { name: "Thinker",   description: "Reads the prompt and classifies intent, complexity and entities.",                                status: "shipping" },
      { name: "Planner",   description: "Breaks a task into ordered steps, each tagged with the kind of specialist it needs.",            status: "shipping" },
      { name: "Router",    description: "Matches every step to the best-fit agent and the right depth of reasoning.",                     status: "shipping" },
      { name: "Executor",  description: "Runs the plan step by step, streaming each event so you can watch the work happen.",             status: "shipping" },
      { name: "Reflector", description: "Reviews the finished result against the brief and asks for a retry when quality is short.",      status: "shipping" },
      { name: "Ingestor",  description: "Pulls relevant memory, prior work and uploaded files into context before the model sees them.",  status: "shipping" },
      { name: "A2A Bridge", description: "Speaks the open agent-to-agent protocol so external agents can ask askFinz to do real work.",   status: "beta" },
    ],
  },

  // ─── Research ─────────────────────────────────────────────────────────────
  {
    kicker: "Research",
    surface: "research.askfinz.ai",
    heading: "Inside Research.",
    lede:
      "A reading room that browses for you, argues with itself, and writes only what it can cite.",
    agents: [
      { name: "Deep Researcher",   description: "Plans, browses and synthesises a long-form study, streaming every step.",                            status: "shipping" },
      { name: "Council",           description: "Runs the same prompt across several minds in parallel, then a judge merges the strongest drafts.",   status: "shipping" },
      { name: "Critic",            description: "Generator + reviewer pair — scores a draft against a rubric and sends it back for one revision.",    status: "shipping" },
      { name: "Source Matrix",     description: "Reads N sources across M questions and fills a comparison grid with a confidence score per cell.",   status: "shipping" },
      { name: "Citation Lens",     description: "Pulls inbound citations for any source and labels each one as supporting, contradicting or neutral.", status: "shipping" },
      { name: "Reviewer",          description: "Lint, gap analysis and rubric scoring on a draft — surfaces uncited claims and missing sections.",    status: "shipping" },
      { name: "Library Trainer",   description: "Licences public-library documents for training, settles royalties and tracks owner credits.",         status: "beta" },
      { name: "Template Extractor", description: "Reads any document and proposes a reusable structure you can apply to the next study.",              status: "shipping" },
      { name: "Suggestion Reviewer", description: "Turns tracked-change proposals into accept/reject decisions with an audit trail.",                  status: "shipping" },
    ],
  },

  // ─── Code ─────────────────────────────────────────────────────────────────
  {
    kicker: "Code",
    surface: "code.askfinz.ai",
    heading: "Inside Code.",
    lede:
      "A pair-programmer that lives in the editor and a small army of specialists you can call when the work needs more than one head.",
    agents: [
      { name: "Code Agent",       description: "Pairs in the editor — reads the repo, edits files, runs commands, answers in your terminal.",        status: "shipping" },
      { name: "Inline Agent",     description: "Highlight a span of code and ask for the rewrite right there; preview the diff before accepting.",   status: "shipping" },
      { name: "Swarm",            description: "Run several code agents at once on the same prompt or on independent slices of a task.",             status: "shipping" },
      { name: "Coordinator",      description: "Lets the main agent spawn sub-agents on its own and weave their answers into one reply.",            status: "shipping" },
      { name: "Sub-agent Spawner", description: "Drops a focused agent onto a sub-task with its own scratchpad, then hands the result back.",        status: "shipping" },
      { name: "Ultraplan",        description: "Multi-agent planning — parallel planners propose approaches, the best parts get merged.",            status: "beta" },
      { name: "Batch Worker",     description: "Runs thousands of prompts through a model in one cheap overnight job and streams results as ready.", status: "shipping" },
      { name: "Skills",           description: "Reusable role packs (refactor, test, review, debug, security, optimise) the agent loads on demand.", status: "shipping" },
      { name: "Hooks Runner",     description: "Fires shell commands before, during or after a tool call so your project rules stay enforced.",      status: "shipping" },
      { name: "Tool Server",      description: "Exposes the workspace as a tool registry so other agents — even ones outside askFinz — can call it.", status: "shipping" },
      { name: "Live Collaborator", description: "Cursor presence, real-time cursors and shared selections when more than one person is in the file.", status: "beta" },
      { name: "Bug Hunter",       description: "A specialised mode that hardens its prompt for reproducing, isolating and fixing nasty bugs.",       status: "shipping" },
    ],
  },

  // ─── Chat ─────────────────────────────────────────────────────────────────
  {
    kicker: "Chat",
    surface: "chat.askfinz.ai",
    heading: "Inside Chat.",
    lede:
      "Specialists you can summon by role inside any conversation — and one general-purpose mind that knows when to hand off.",
    agents: [
      { name: "Researcher",   description: "Reads broadly, cross-references claims, cites sources and structures the answer.",           status: "shipping" },
      { name: "Coder",        description: "Generates, reviews, debugs and refactors code in any major language.",                       status: "shipping" },
      { name: "Writer",       description: "Drafts, edits and rewrites to a target tone — clean structure, plain language.",             status: "shipping" },
      { name: "Analyst",      description: "Works with numbers — tables, comparisons, charts and quantitative reasoning.",               status: "shipping" },
      { name: "Summariser",   description: "Distils long content into the essentials with a structured outline and TL;DR.",              status: "shipping" },
      { name: "Advisor",      description: "Secondary mind running alongside the primary, surfacing alternative suggestions as you go.", status: "beta" },
    ],
  },

  // ─── News ─────────────────────────────────────────────────────────────────
  {
    kicker: "News",
    surface: "news.askfinz.ai",
    heading: "Inside News.",
    lede:
      "Crawls, deduplicates, listens — turns the firehose into a single briefing and a daily audio version you can play.",
    agents: [
      { name: "Crawler",       description: "Reaches into hundreds of publishers, extracts full articles and keeps the index fresh.",             status: "shipping" },
      { name: "Analyst",       description: "Sentiment, bias and category — every story scored so the briefing reads honestly.",                  status: "shipping" },
      { name: "Briefing Host", description: "Builds the hourly transcript across today's top stories and renders it as audio.",                   status: "shipping" },
      { name: "Risk Watcher",  description: "Scans the headline window for sector and ticker risk signals over a rolling fourteen-day view.",     status: "beta" },
    ],
  },

  // ─── Browser ──────────────────────────────────────────────────────────────
  {
    kicker: "Browser",
    surface: "Cross-app capability",
    heading: "Inside Browser.",
    lede:
      "A real browser an agent can drive — for the times the web won't give you an API.",
    agents: [
      { name: "Navigator",     description: "Drives a real, headed browser to read pages, follow flows and fill forms — with a screencast.",   status: "shipping" },
      { name: "Proxy Manager", description: "Rotates clean exit-IPs and degrades gracefully so a brittle site doesn't end the session.",       status: "shipping" },
    ],
  },

  // ─── Search ───────────────────────────────────────────────────────────────
  {
    kicker: "Search",
    surface: "search.askfinz.ai",
    heading: "Inside Search.",
    lede:
      "askFinz's own search index — keeps every workspace findable for humans, agents and other models.",
    agents: [
      { name: "Indexer",        description: "Reads every workspace and keeps the askFinz vector index in sync as you work.",           status: "shipping" },
      { name: "Index Reviewer", description: "Sweeps the index for stale, broken or low-quality entries and quietly heals them.",       status: "shipping" },
    ],
  },

  // ─── Workflow ────────────────────────────────────────────────────────────
  {
    kicker: "Workflow",
    surface: "workflow.askfinz.ai",
    heading: "Inside Workflow.",
    lede:
      "Drag-and-drop flows that the platform can execute on a schedule or on demand.",
    agents: [
      { name: "Workflow Runner", description: "Executes a custom flow you've designed — connects steps, ships the output, logs the run.", status: "shipping" },
    ],
  },

  // ─── Memory ──────────────────────────────────────────────────────────────
  {
    kicker: "Memory",
    surface: "Cross-app capability",
    heading: "Inside Memory.",
    lede:
      "What every other agent leans on so it doesn't forget you between sessions.",
    agents: [
      { name: "Memory Keeper",  description: "Saves the facts you ask it to remember and pulls them back into future conversations.",         status: "shipping" },
      { name: "Team Memory",    description: "Shares a project-scoped memory across collaborators with last-write-wins reconciliation.",      status: "shipping" },
      { name: "Dreamer",        description: "Once a week, distils recent session logs into durable project memory you can review.",          status: "beta" },
      { name: "Memory Searcher", description: "Semantic recall across everything memory has ever held — surfaces the right fact, fast.",     status: "shipping" },
    ],
  },

  // ─── Safety ──────────────────────────────────────────────────────────────
  {
    kicker: "Safety",
    surface: "Cross-app capability",
    heading: "Inside Safety.",
    lede:
      "The pieces that say no on your behalf — so a fast agent never becomes a destructive one.",
    agents: [
      { name: "Permissions",   description: "Per-tool approval rules with wildcard allow/deny so risky actions always pause for you.",        status: "shipping" },
      { name: "Diff Reviewer", description: "Queues every file write for inspection before it lands — accept, reject or rewrite the patch.",  status: "shipping" },
      { name: "Sandbox",       description: "Restricts which files and domains an agent may touch, even when it is told to do more.",         status: "shipping" },
      { name: "Verifier",      description: "Cross-checks an action against your stated intent before money is spent or content is sent.",    status: "beta" },
    ],
  },
];

export function totalAgentCount(): number {
  return AGENT_GROUPS.reduce((n, g) => n + g.agents.length, 0);
}

export function countsByGroup(): Array<{ kicker: string; surface?: string; count: number }> {
  return AGENT_GROUPS.map((g) => ({ kicker: g.kicker, surface: g.surface, count: g.agents.length }));
}


/* ────────────────────────────────────────────────────────────────────────────
   FEATURED SLICE — the specialists shown on /platform and counted by the
   platform pillar grid.

   It lived inside app/platform/page.tsx, so platform-pillar-grid.tsx could not
   see it and simply asserted "Twelve specialists" in prose. Two places stating
   the length of a list only one of them could read is a guaranteed drift; both
   now count this array.
   ──────────────────────────────────────────────────────────────────────────── */
export type FeaturedAgent = { name: string; line: string; href?: string };

export const FEATURED_AGENTS: FeaturedAgent[] = [
  { name: "Researcher", line: "Synthesises web pages, PDFs, prior notebooks and live feeds — every claim attached to the page it came from.", href: "/apps/research" },
  { name: "Coder",      line: "Pairs in a full IDE — refactors, generates tests, runs the suite, writes the design doc next to the diff.",   href: "/apps/code" },
  { name: "Writer",     line: "Long-form drafts, memos and briefs that hold their structure across thousands of words.",                     href: "/apps/chat" },
  { name: "Analyst",    line: "Explores data, runs regressions and builds the chart that actually answers the question.",                    href: "/apps/data" },
  { name: "Summariser", line: "Distils long meetings, long documents and long threads down to what someone needs to know in two minutes.",   href: "/apps/chat" },
  { name: "Browser",    line: "Drives a sandboxed live web session — fills forms, finishes sessions, returns the artefact with screenshots.",href: "/agents" },
  { name: "Mailer",     line: "Triages the inbox, drafts the replies, schedules the sends — your voice, your timing.",                       href: "/apps/mail" },
  { name: "Trainer",    line: "Runs fine-tunes and eval sweeps end to end; you watch the curves, you ship the result.",                      href: "/apps/train" },
  { name: "Workflow",   line: "Turns a sequence of moves into a visual pipeline that runs on a schedule — triggers, retries, the AI step are nodes on a canvas.", href: "/apps/workflow" },
  { name: "Search",     line: "askFinz's own LLM-friendly search engine — clean answers with sources, ready to drop into a draft.",          href: "/apps/search" },
  { name: "Socials",    line: "Drafts and schedules across multiple accounts — one voice per channel, the calendar in one place.",           href: "/apps/socials" },
  { name: "Finance",    line: "Portfolio-aware drafting and analysis — briefings, notes and charts that know what you hold.",                href: "/apps/finance" },
];

/** Spelled-out counts, for headings where a digit reads wrong. */
const NUM_WORDS = ["", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine",
  "Ten", "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen"];
export const countWord = (n: number): string => NUM_WORDS[n] ?? String(n);

/** Total agents as a FLOOR ("50+"), for copy that should survive the roster changing by one. */
export const agentFloor = (): string => `${Math.floor(totalAgentCount() / 10) * 10}+`;
